// Formatação dos números da área do afiliado (/{idioma}/afiliado).
//
// Sem Intl de propósito, como o shared/utils/money.ts: o Intl muda entre Node e
// navegador conforme o ICU, e a diferença vira mismatch de hidratação. Os nomes
// levam "Area" porque o Nuxt importa os utils sozinho e o afiliadosSimulador já
// exporta um `formatarMoeda`.

export type IdiomaArea = "pt-br" | "en";

function separadores(idioma: IdiomaArea) {
  return idioma === "en" ? { milhar: ",", decimal: "." } : { milhar: ".", decimal: "," };
}

function comMilhar(inteiro: string, milhar: string): string {
  return inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, milhar);
}

function fixo(valor: number, casas: number, idioma: IdiomaArea): string {
  const { milhar, decimal } = separadores(idioma);
  const numero = Number.isFinite(valor) ? valor : 0;
  const sinal = numero < 0 ? "-" : "";
  const [inteiro, fracao] = Math.abs(numero).toFixed(casas).split(".");

  return sinal + comMilhar(inteiro, milhar) + (fracao ? decimal + fracao : "");
}

/** 18240 → "18.240" (pt-br) ou "18,240" (en). */
export function formatarNumeroArea(valor: number, idioma: IdiomaArea): string {
  return fixo(Math.round(valor), 0, idioma);
}

/** Comissão é sempre em real: "R$ 1.234,56" ou "R$ 1,234.56". */
export function formatarMoedaArea(valor: number, idioma: IdiomaArea): string {
  return "R$ " + fixo(valor, 2, idioma);
}

/** 12.8 → "12,8%". Sem valor, "—". */
export function formatarPercentualArea(valor: number | null | undefined, idioma: IdiomaArea): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";

  return fixo(valor, 1, idioma) + "%";
}

/**
 * Conversão da tabela de planos (compras ÷ acessos, em %). Com o tráfego real ela fica em
 * centésimos de ponto: abaixo de 1% vão 2 casas ("0,05%") e abaixo de 0,01% vai "< 0,01%". Sem
 * compra, "0%"; sem acesso no período, "—".
 */
export function formatarConversaoArea(valor: number | null | undefined, idioma: IdiomaArea): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";
  if (valor <= 0) return "0%";
  if (valor < 0.01) return "< " + fixo(0.01, 2, idioma) + "%";

  // 0,996 arredonda para 1,00: a partir daí, 1 casa como os demais percentuais.
  return fixo(valor, Math.round(valor * 100) < 100 ? 2 : 1, idioma) + "%";
}

/** Variação do card: texto com sinal e se é queda. Sem base de comparação, null. */
export function variacaoArea(
  valor: number | null | undefined,
  idioma: IdiomaArea
): { texto: string; queda: boolean } | null {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return null;

  return { texto: formatarPercentualArea(valor, idioma), queda: valor < 0 };
}

/** "Camila Lopes" → "CL"; "Duda" → "DU", como no template. */
export function iniciaisArea(nome: string | null | undefined): string {
  const partes = String(nome ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!partes.length) return "";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();

  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/** O link aparece sem "https://", como no template; o copiado vai completo. */
export function semProtocoloArea(url: string | null | undefined): string {
  return String(url ?? "").replace(/^https?:\/\//, "");
}

/** "Olá, {nome}." com { nome: "Joana" } → "Olá, Joana.". Chave sem valor fica como está. */
export function preencherArea(texto: string, valores: Record<string, string | number>): string {
  return texto.replace(/\{(\w+)\}/g, (inteiro, chave) =>
    chave in valores ? String(valores[chave]) : inteiro
  );
}
